import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { useCallback, useState } from "react";
import { useAppContext } from "../../../providers/ProviderAppContainer";
import TabHeader from "../../common/TabHeader";
import FileInput from "../../common/FileInput";
import { FloatLabel } from "primereact/floatlabel";
import { Calendar } from "primereact/calendar";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import HasRequiredAuthority from "../../dependencies/HasRequiredAuthority";
import { AUTHORITIES } from "../../../constants";
import { getWriteableDate } from "../../../utils";
import { TEXT_NORMAL, TEXT_TITLE } from "../../../style";

export default function DialogAddCoursePromoDialogContent({ visible, closeDialog, courseId, setContents }) {
    const { requestAPI, showToast } = useAppContext();
    const [form, setForm] = useState({
        daily: false,
        frequency: 1,
        active: true,
        interval: 0,
        redirect_url: "",
    });
    const [loading, setLoading] = useState();

    const addContent = useCallback(() => {
        if (!form?.content) {
            showToast({ severity: "warn", summary: "Content", detail: "Please add media content", life: 2000 });
            return;
        }

        if (!form?.start_date || !form?.end_date) {
            showToast({ severity: "warn", summary: "Dates", detail: "Please select start and end dates", life: 2000 });
            return;
        }

        if (!form?.frequency || form.frequency < 1) {
            showToast({ severity: "warn", summary: "Frequency", detail: "Frequency must be at least 1", life: 2000 });
            return;
        }

        requestAPI({
            requestPath: "dialog-contents/courses",
            requestMethod: "POST",
            requestPostBody: {
                course_id: courseId,
                content: form.content,
                redirect_url: form.redirect_url?.trim() || null,
                start_date: getWriteableDate({ date: form.start_date, removeTime: true }),
                end_date: getWriteableDate({ date: form.end_date, removeTime: true }),
                daily: !!form.daily,
                frequency: form.frequency,
                active: !!form.active,
                interval: form.interval ?? 0,
            },
            setLoading: setLoading,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Add Content !", life: 2000 }),
            onResponseReceieved: ({ error, ...item }, responseCode) => {
                if (item?.id && responseCode === 201) {
                    showToast({ severity: "success", summary: "Added", detail: "Content Added", life: 1000 });
                    setContents((prev) => [...prev, item].sort((a, b) => a.view_index - b.view_index || a.id - b.id));
                    setForm({ daily: false, frequency: 1, active: true, interval: 0, redirect_url: "" });
                    closeDialog();
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: error || "Failed To Add Content !", life: 2000 });
                }
            },
        });
    }, [closeDialog, courseId, form, requestAPI, setContents, showToast]);

    return (
        <Dialog
            header="Add Promo Content"
            visible={visible}
            className="w-11"
            onHide={closeDialog}
            pt={{
                headertitle: { className: TEXT_TITLE },
                content: { className: "overflow-y-scroll", style: { maxHeight: "70vh" } },
            }}
        >
            <TabHeader className="pt-3" title="Add Promo Content" highlights={["Images & GIFs can be used as promo slides"]} />

            <FileInput
                className="mt-3"
                label="Promo Media"
                type="image"
                cdn_url={form?.content}
                setCDNUrl={(cdn_url) => setForm((prev) => ({ ...prev, content: cdn_url }))}
                disabled={loading}
            />

            <FloatLabel className="mt-5">
                <Calendar
                    dateFormat="dd/mm/yy"
                    inputId="course_promo_start_date"
                    className="w-full"
                    value={form?.start_date}
                    onChange={(e) => setForm((prev) => ({ ...prev, start_date: e.value }))}
                    disabled={loading}
                    maxDate={form?.end_date}
                    showIcon
                    pt={{ input: { root: { className: TEXT_NORMAL } } }}
                />
                <label htmlFor="course_promo_start_date" className={TEXT_NORMAL}>
                    Start Date
                </label>
            </FloatLabel>

            <FloatLabel className="mt-5">
                <Calendar
                    dateFormat="dd/mm/yy"
                    inputId="course_promo_end_date"
                    className="w-full"
                    value={form?.end_date}
                    onChange={(e) => setForm((prev) => ({ ...prev, end_date: e.value }))}
                    disabled={loading}
                    minDate={form?.start_date}
                    showIcon
                    pt={{ input: { root: { className: TEXT_NORMAL } } }}
                />
                <label htmlFor="course_promo_end_date" className={TEXT_NORMAL}>
                    End Date
                </label>
            </FloatLabel>

            <FloatLabel className="mt-5">
                <InputNumber
                    inputId="course_promo_frequency"
                    className="w-full"
                    value={form?.frequency}
                    onValueChange={(e) => setForm((prev) => ({ ...prev, frequency: e.value }))}
                    disabled={loading}
                    min={1}
                    pt={{ input: { root: { className: TEXT_NORMAL } } }}
                />
                <label htmlFor="course_promo_frequency" className={TEXT_NORMAL}>
                    Frequency
                </label>
            </FloatLabel>

            <FloatLabel className="mt-5">
                <InputNumber
                    inputId="course_promo_interval"
                    className="w-full"
                    value={form?.interval}
                    onValueChange={(e) => setForm((prev) => ({ ...prev, interval: e.value ?? 0 }))}
                    disabled={loading}
                    min={0}
                    pt={{ input: { root: { className: TEXT_NORMAL } } }}
                />
                <label htmlFor="course_promo_interval" className={TEXT_NORMAL}>
                    Interval (seconds)
                </label>
            </FloatLabel>

            <FloatLabel className="mt-5">
                <InputText
                    id="course_promo_redirect_url"
                    className="w-full"
                    value={form?.redirect_url ?? ""}
                    onChange={(e) => setForm((prev) => ({ ...prev, redirect_url: e.target.value }))}
                    disabled={loading}
                />
                <label htmlFor="course_promo_redirect_url" className={TEXT_NORMAL}>
                    Redirect URL (optional)
                </label>
            </FloatLabel>

            <div className="flex align-items-center justify-content-between mt-5">
                <span className={TEXT_NORMAL}>Daily limit</span>
                <InputSwitch checked={!!form?.daily} onChange={(e) => setForm((prev) => ({ ...prev, daily: e.value }))} disabled={loading} />
            </div>

            <div className="flex align-items-center justify-content-between mt-3">
                <span className={TEXT_NORMAL}>Active</span>
                <InputSwitch checked={!!form?.active} onChange={(e) => setForm((prev) => ({ ...prev, active: e.value }))} disabled={loading} />
            </div>

            <HasRequiredAuthority requiredAuthority={AUTHORITIES.UPDATE_COURSE}>
                <Button
                    className="mt-4"
                    label="Add Content"
                    severity="warning"
                    loading={loading}
                    disabled={!form?.content}
                    onClick={addContent}
                    pt={{ label: { className: TEXT_NORMAL } }}
                />
            </HasRequiredAuthority>
        </Dialog>
    );
}
